import { type ReactNode, useEffect } from "react";

import { useToast } from "@hightouch/htui";
import { generatePath, useNavigate, useParams } from "src/router";

import type { V2ResourceToPermission } from "src/components/permission/types-v2";
import { PageContentSkeleton } from "src/components/loading";
import {
  type ResourcePermissionInput,
  useResourcePermission,
} from "src/components/permission/use-resource-permission";
import { useUser } from "src/contexts/user-context";

type Props<V2Resource extends V2ResourceToPermission> = {
  permission: ResourcePermissionInput<V2Resource>;
  requireImpersonationPrivileges?: boolean;
  // Let impersonating Hightouch staff through even when the permission
  // check fails (e.g. read-only impersonation on admin-gated pages).
  allowImpersonators?: boolean;
  children: ReactNode;
  redirect: string;
};

export function PermissionedRoute<V2Resource extends V2ResourceToPermission>({
  children,
  permission,
  requireImpersonationPrivileges = false,
  allowImpersonators = false,
  redirect,
}: Props<V2Resource>) {
  const params = useParams();

  const input = { ...permission };

  if ("v2" in input && "id" in input.v2 && input.v2.id) {
    input.v2 = { ...input.v2, id: params[input.v2.id] };
  }

  const { isPermitted: isPermittedByCheck, isLoading } =
    useResourcePermission(input);
  const navigate = useNavigate();
  const { user } = useUser();
  const { toast } = useToast();

  const isPermitted =
    isPermittedByCheck || (allowImpersonators && !!user?.is_impersonating);
  const isMissingImpersonationPrivileges =
    requireImpersonationPrivileges && !user?.can_impersonate;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if ((!isLoading && !isPermitted) || isMissingImpersonationPrivileges) {
      const to = generatePath(redirect, params);
      toast({
        id: "unauthorized-navigation",
        title: "You do not have permission to view this page",
        variant: "warning",
      });
      navigate(to);
    }
  }, [permission, isLoading, isMissingImpersonationPrivileges]);

  if (isLoading || !permission || isMissingImpersonationPrivileges) {
    return <PageContentSkeleton />;
  }

  return <>{children}</>;
}
